/**
 * Placement engine: runs the fixed-order placement sequence from PRD §7.4.
 *
 *   1. Load room exclusion zones (handled by caller passing roomDefinition)
 *   2. Select dance floor size
 *   3. Place dance floor + DJ booth as one combined unit
 *   4. Place head table or sweetheart table
 *   5. Place feature zones (gift, cake, dessert, photo booth, donut wall)
 *   6. Define the main aisle (protected corridor, not an object — used only
 *      to carve the usable floor for step 7)
 *   7. Fill remaining floor with guest tables
 *   8. (Validation runs separately, in validation/validateLayout.ts)
 *
 * This module composes seatingMath.ts, danceFloor.ts, and patterns.ts. It
 * has no framework dependencies — same guarantee as the rest of domain/.
 */

import { v4 as uuidv4 } from 'uuid';
import type {
  GenerationInput,
  GenerationResult,
  LayoutObject,
  ObjectType,
} from '../types';
import { buildSeatingPlan } from './seatingMath';
import { decideDanceFloorSize } from '../danceFloor';
import {
  layoutRoundTablesOffsetRows,
  layoutRectTablesParallelRows,
  layoutCocktailTablesScattered,
  type UsableFloor,
} from './patterns';
import { getCatalogEntry } from '../inventory';

const MAIN_AISLE_DEPTH_FT = 5; // matches PRD §8.1's 5ft main aisle minimum exactly
const FEATURE_ZONE_BAND_FT = 3; // gift/photo booth tables are shallow (~2.5-3ft deep)
const DANCE_FLOOR_BACK_MARGIN_FT = 1.5; // clearance behind dance floor to the back wall
const HEAD_TABLE_GAP_FT = 2.5; // gap between dance floor and head table; head table is single-sided, doesn't need a deep buffer
const TRAILING_FLOOR_MARGIN_FT = 1; // small margin between head table zone and guest tables

let labelCounters: Record<string, number> = {};

function nextLabel(prefix: string): string {
  labelCounters[prefix] = (labelCounters[prefix] ?? 0) + 1;
  return `${prefix} ${labelCounters[prefix]}`;
}

function resetLabelCounters() {
  labelCounters = {};
}

function makeObject(
  type: ObjectType,
  x: number,
  y: number,
  rotation: number,
  label: string,
  seats: number | null,
  zIndex: number
): LayoutObject {
  return {
    id: uuidv4(),
    type,
    x,
    y,
    rotation,
    label,
    seats,
    locked: false,
    zIndex,
  };
}

export function generateLayout(input: GenerationInput): GenerationResult {
  resetLabelCounters();
  const notes: string[] = [];
  const objects: LayoutObject[] = [];
  const { roomDefinition, layoutStyle, guestCount, headTableType, headTablePartySize } = input;

  const seatingPlan = buildSeatingPlanSafe(input);

  // --- Step 2-3: Dance floor + DJ booth, anchored to the focal wall
  // (the wall opposite the entrance, so guests face it on entry).
  const danceFloorDecision = decideDanceFloorSize(guestCount, roomDefinition);
  if (danceFloorDecision.steppedDown) {
    notes.push('Dance floor sized down to 15\u2019x15\u2019 to fit guest seating comfortably in this room.');
  }
  const danceFloorEntry = getCatalogEntry(danceFloorDecision.size, input.objectCatalog);
  const focalY = roomDefinition.lengthFt - danceFloorEntry.defaultHeightFt - DANCE_FLOOR_BACK_MARGIN_FT;
  const danceFloorX = roomDefinition.widthFt / 2 - danceFloorEntry.defaultWidthFt / 2;

  objects.push(
    makeObject(
      danceFloorDecision.size,
      danceFloorX,
      focalY,
      0,
      'Dance Floor',
      null,
      1
    )
  );

  const djEntry = getCatalogEntry('dj_booth', input.objectCatalog);
  objects.push(
    makeObject(
      'dj_booth',
      danceFloorX + danceFloorEntry.defaultWidthFt + 1,
      focalY + danceFloorEntry.defaultHeightFt - djEntry.defaultHeightFt,
      0,
      'DJ Setup',
      null,
      1
    )
  );

  // --- Step 4: Head table or sweetheart table, between the entrance band
  // and the dance floor, facing into the room (single-sided, no chairs on
  // the back side away from guests).
  const headTableY = focalY - HEAD_TABLE_GAP_FT;
  if (headTableType === 'sweetheart_table') {
    const entry = getCatalogEntry('sweetheart_table', input.objectCatalog);
    objects.push(
      makeObject(
        'sweetheart_table',
        roomDefinition.widthFt / 2 - entry.defaultWidthFt / 2,
        headTableY,
        0,
        'Sweetheart Table',
        entry.defaultSeats,
        2
      )
    );
  } else {
    const entry = getCatalogEntry('head_table', input.objectCatalog);
    const partySize = headTablePartySize ?? entry.defaultSeats ?? 6;
    objects.push(
      makeObject(
        'head_table',
        roomDefinition.widthFt / 2 - entry.defaultWidthFt / 2,
        headTableY,
        0,
        'Head Table',
        partySize,
        2
      )
    );
  }

  // --- Step 5: Feature zones along the entrance band.
  // Gift table near entrance; photo booth perimeter; cake table near head
  // table sightline; dessert station perimeter, away from the cake table;
  // donut wall flagged for manual placement (needs a confirmed flat wall).
  const entranceBandY = 2;

  const giftEntry = getCatalogEntry('gift_table', input.objectCatalog);
  objects.push(
    makeObject('gift_table', 3, entranceBandY, 0, 'Gift Table', null, 1)
  );
  void giftEntry; // fetched for future use (e.g. validating it has its own footprint); position is fixed near entrance per PRD §6

  const photoEntry = getCatalogEntry('photo_booth', input.objectCatalog);
  objects.push(
    makeObject(
      'photo_booth',
      roomDefinition.widthFt - photoEntry.defaultWidthFt - 3,
      entranceBandY,
      0,
      'Photo Booth',
      null,
      1
    )
  );

  const cakeEntry = getCatalogEntry('cake_table', input.objectCatalog);
  objects.push(
    makeObject(
      'cake_table',
      roomDefinition.widthFt / 2 + 8,
      headTableY,
      0,
      'Cake Table',
      null,
      1
    )
  );
  void cakeEntry;

  const dessertEntry = getCatalogEntry('dessert_station', input.objectCatalog);
  objects.push(
    makeObject(
      'dessert_station',
      3,
      roomDefinition.lengthFt / 2,
      0,
      'Dessert Station',
      null,
      1
    )
  );
  void dessertEntry;

  // Donut wall: only auto-placed if there's a plausible flat wall run;
  // otherwise included but flagged for the coordinator to manually place.
  const donutEntry = getCatalogEntry('donut_wall', input.objectCatalog);
  const hasWallRoomForDonutWall = roomDefinition.widthFt >= 30;
  objects.push(
    makeObject(
      'donut_wall',
      roomDefinition.widthFt - donutEntry.defaultWidthFt - 3,
      roomDefinition.lengthFt / 2,
      0,
      'Donut Wall',
      null,
      1
    )
  );
  if (!hasWallRoomForDonutWall) {
    notes.push('Donut Wall placed automatically, but this room may need manual repositioning to find a clear wall run.');
  }

  // --- Steps 6-7: Main aisle + guest table fill.
  // Usable guest-table floor excludes the entrance/feature band, the
  // head table zone, and the dance floor/DJ zone.
  const usableFloor: UsableFloor = {
    x: 2,
    y: entranceBandY + FEATURE_ZONE_BAND_FT + MAIN_AISLE_DEPTH_FT,
    width: roomDefinition.widthFt - 4,
    height: headTableY - (entranceBandY + FEATURE_ZONE_BAND_FT + MAIN_AISLE_DEPTH_FT) - TRAILING_FLOOR_MARGIN_FT,
  };

  if (layoutStyle === 'long_table_feast') {
    const positions = layoutRectTablesParallelRows(seatingPlan.rectTable8Count, 8, usableFloor);
    if (positions.length < seatingPlan.rectTable8Count) {
      notes.push(
        'This guest count may not fit comfortably as Long Table Feast in this room. Consider Classic Banquet or a larger room.'
      );
    }
    positions.forEach((pos) => {
      objects.push(
        makeObject('rect_table_8', pos.x, pos.y, pos.rotation, nextLabel('Table'), 8, 3)
      );
    });
  } else if (layoutStyle === 'lounge_cocktail') {
    const roundPositions = layoutRoundTablesOffsetRows(seatingPlan.roundTableCount, usableFloor);
    roundPositions.forEach((pos) => {
      objects.push(makeObject('round_table', pos.x, pos.y, pos.rotation, nextLabel('Table'), 8, 3));
    });
    const cocktailFloor: UsableFloor = {
      ...usableFloor,
      y: usableFloor.y + usableFloor.height * 0.6,
      height: usableFloor.height * 0.4,
    };
    const cocktailPositions = layoutCocktailTablesScattered(seatingPlan.cocktailTableCount, cocktailFloor);
    cocktailPositions.forEach((pos) => {
      objects.push(makeObject('cocktail_table', pos.x, pos.y, pos.rotation, nextLabel('Cocktail Table'), null, 3));
    });
  } else {
    // classic_banquet, mixed_modern
    const positions = layoutRoundTablesOffsetRows(seatingPlan.roundTableCount, usableFloor);
    if (positions.length < seatingPlan.roundTableCount) {
      notes.push('Not all guest tables fit within the room at full clearance — review the layout for tight spots.');
    }
    positions.forEach((pos) => {
      objects.push(makeObject('round_table', pos.x, pos.y, pos.rotation, nextLabel('Table'), 8, 3));
    });
  }

  return { objects, notes };
}

/** Wrapper so generateLayout can call buildSeatingPlan without importing
 * GenerationInput's exact shape into seatingMath.ts (keeps that module's
 * signature minimal and independently testable). */
function buildSeatingPlanSafe(input: GenerationInput) {
  return buildSeatingPlan(
    input.guestCount,
    input.headTableType,
    input.headTablePartySize,
    input.layoutStyle
  );
}
